import React, { useEffect, useMemo, useState } from 'react';
import { Loader } from '@mantine/core';
import { config } from '@shakkerai/config';
import { translate } from '@shakkerai/util-i18n';
import { setTempState, useTempState } from '@starflow/app/store';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { ImagesWindow } from '../images';
import { ModelImagesWindow } from '../modelImages';
import galleryIconShakker from '../../assets/gallery_icon_shakker.png';
import galleryIconPic from '../../assets/gallery_icon_pic.png';
import { usePartialState } from '../../store/state';
import { ImagesGenerateInfoWindow } from '../info/index';

// fade interval
const fadeInterval = 2000;
const isOverseas = config.env === 'aws';

const galleryIcon = isOverseas ? galleryIconShakker : galleryIconPic;

const ImagesLoop = React.memo(({ images }) => {
  const renderedImages = useMemo(() => {
    return images.concat(images[0]);
  }, [images]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const prevIndex = currentIndex === 0 ? renderedImages.length - 1 : currentIndex - 1;
  const nextIndex = currentIndex === renderedImages.length - 1 ? 0 : currentIndex + 1;
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;

        if (nextIndex >= renderedImages.length - 1) {
          clearInterval(interval);
        }

        return nextIndex;
      });
    }, fadeInterval);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 图片轮流fadein fadeout
  return (
    <div className="relative w-6 h-6">
      {renderedImages.map((item, index) => {
        const isCurrent = index === currentIndex;
        const isPrev = index === prevIndex;
        const isNext = index === nextIndex;

        let extraClassName = '';

        if (isCurrent) {
          extraClassName = 'opacity-100 scale-100';
        } else if (isPrev) {
          extraClassName = 'opacity-0 scale-150';
        } else if (isNext) {
          extraClassName = 'opacity-0 scale-50';
        } else {
          extraClassName = 'opacity-0';
        }

        return (
          <img
            key={index}
            src={convert2CompressedUrl(item.imageUrl)}
            className={`absolute w-6 h-6 rounded-full object-cover ease-in-out transition duration-700 ${extraClassName}`}
          />
        );
      })}
    </div>
  );
});

export const ImagesEntry = React.memo(() => {
  const recommendImages = usePartialState('recommendImages.data');
  const imagesWindowOpenedState = useTempState('imagesWindowOpened');

  if (recommendImages.loading) {
    return (
      <div className="flex items-center h-[34px] px-3 bg-white rounded-sm shadow-light flex-shrink-0">
        <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
          <Loader size={14} type="dots" />
        </div>
        <span className="flex-shrink-0 ml-3 text-xs text-black">{translate('gallery_title')}</span>
      </div>
    );
  }

  const handleOpenImages = () => {
    if (imagesWindowOpenedState) return;
    setTempState({ showNormalCkptGide: false, imagesWindowOpened: true });
  };

  return (
    <button
      onClick={handleOpenImages}
      className="flex items-center flex-shrink-0 h-[34px] px-3 bg-white rounded-sm shadow-light"
    >
      {recommendImages.items.length ? (
        <div className="relative flex-shrink-0 w-6 h-6 bg-black/10 rounded-full">
          <ImagesLoop images={recommendImages.items.slice(0, 4)} />
        </div>
      ) : (
        <div className="relative flex-shrink-0 w-[26px] h-[26px]">
          <img src={galleryIcon} alt="" className="w-[26px] h-[26px] rounded-full" />
        </div>
      )}
      <span className="flex-shrink-0 ml-3 text-xs text-black">{translate('gallery_title')}</span>
    </button>
  );
});

export const ImagesWindows = () => {
  const imagesWindowOpened = useTempState('imagesWindowOpened');
  const modelImagesWindowOpened = useTempState('modelImagesWindowOpened');
  const generationWindowId = useTempState('generationWindowId');
  const imageData = useTempState('thumbImageInfo');
  const thumbGenerationInfo = useTempState('thumbGenerationInfo');

  const isShowGenerateInfo = useMemo(() => {
    return generationWindowId && imageData && thumbGenerationInfo;
  }, [generationWindowId, imageData, thumbGenerationInfo]);
  return (
    <>
      {imagesWindowOpened && <ImagesWindow />}
      {modelImagesWindowOpened && <ModelImagesWindow />}
      {isShowGenerateInfo && <ImagesGenerateInfoWindow />}
    </>
  );
};
